/**
 * Table
 *
 * Note: due to the types for react-table seemingly not including types
 * for all of the "optional" hooks (types may be outdated), this project
 * includes a file in src/@types/assets/react-table-config.d.ts to supplement
 * the extra types
 * https://github.com/tannerlinsley/react-table/issues/2970
 *
 * Also may have to do some casting to the custom type of your data, as seen
 * in this file and explained by user secorlade here:
 * https://github.com/tannerlinsley/react-table/discussions/2664
 */

// ---------- IMPORTS ----------
import React from 'react'
import { useTheme } from 'styled-components'

import useMedia from 'hooks/useMedia'
import CMS from '@talus-analytics/library.airtable-cms'
import { useTable, usePagination, useSortBy } from 'react-table'
import { displayOwner } from 'utilities/language'
import { useExploreColMappingData } from 'cmsHooks'
import { ResourceCoreData } from 'dataHooks/types'
import { Cell } from 'react-table'
import * as display from './displayComponents'
import device, { size } from 'utilities/device'

interface StringCell extends Cell {
  cell: {
    value?: string
    row: { original: ResourceCoreData }
  }
}
interface StringArrayCell {
  cell: { value?: string[]; row: { original: ResourceCoreData } }
}

interface TableProps {
  resources: ResourceCoreData[]
}
const Table = ({ resources }: TableProps): JSX.Element => {
  const columnMapData = useExploreColMappingData()

  const theme: any = useTheme()

  // filter cols, to only be shown on large screens
  const filterCols = useMedia({
    queries: [device.laptopL, `(min-width: ${size.laptopL})`],
    values: [
      [],
      [
        {
          Header: CMS.getText(columnMapData, 'Filter 1'),
          accessor: 'data.Filter_1' as keyof ResourceCoreData,
          disableSortBy: true,
          Cell: ({ cell: { value } }: StringArrayCell) =>
            value ? value.join(', ') : null,
        },
        {
          Header: CMS.getText(columnMapData, 'Filter 2'),
          accessor: 'data.Filter_2' as keyof ResourceCoreData,
          disableSortBy: true,
          Cell: ({ cell: { value } }: StringArrayCell) =>
            value ? value.join(', ') : null,
        },
      ],
    ],
    defaultValue: [],
  })

  // topic and data cols, to only be shown on laptop-size or bigger screens
  const topicCol = useMedia({
    queries: [device.laptop, `(min-width: ${size.laptop})`],
    values: [
      [],
      [
        {
          Header: CMS.getText(columnMapData, 'Topic'),
          accessor: 'data.Topic' as keyof ResourceCoreData,
          disableSortBy: true,
        },
      ],
    ],
    defaultValue: [],
  })
  const dataCol = useMedia({
    queries: [device.laptop, `(min-width: ${size.laptop})`],
    values: [
      [],
      [
        {
          Header: CMS.getText(columnMapData, 'Data access'),
          accessor: 'data.Data_access' as keyof ResourceCoreData,
          Cell: ({ cell: { value } }: StringCell) =>
            value ? (
              <display.StyledTemplateLink
                as="a"
                href={value?.split('\n')[0]}
                target="_blank"
              >
                Access link
              </display.StyledTemplateLink>
            ) : null,
          disableSortBy: true,
        },
      ],
    ],
    defaultValue: [],
  })

  // owner and description cols, to only be shown on tablet-size and bigger
  const ownerDescripCols = useMedia({
    queries: [device.tablet, `(min-width: ${size.tablet})`],
    values: [
      [],
      [
        {
          Header: CMS.getText(columnMapData, 'Owner'),
          accessor: 'data.Owner' as keyof ResourceCoreData,
          Cell: ({ cell: { value } }: StringArrayCell) =>
            value ? displayOwner(value) : null,
          disableSortBy: true,
        },
        {
          Header: CMS.getText(columnMapData, 'Description'),
          accessor: 'data.Description' as keyof ResourceCoreData,
          disableSortBy: true,
        },
      ],
    ],
    defaultValue: [],
  })

  // cols to pass to table component
  const columns = React.useMemo(
    () => [
      {
        Header: CMS.getText(columnMapData, 'Title'),
        accessor: 'data.Title' as keyof ResourceCoreData, // accessor is the "key" in the data
        Cell: ({ cell: { row, value } }: StringCell) => {
          return (
            <display.StyledTemplateLink data={row.original.data} openInNewTab>
              <strong>{value}</strong>
            </display.StyledTemplateLink>
          )
        },
      },
      ...ownerDescripCols,
      ...topicCol,
      ...filterCols,
      ...dataCol,
    ],
    [ownerDescripCols, topicCol, filterCols, dataCol]
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable<ResourceCoreData>(
    { columns, data: resources, initialState: { pageIndex: 0 } },
    useSortBy,
    usePagination
  )

  return (
    <>
      <display.StyledTable {...getTableProps()}>
        <display.StyledThead>
          {headerGroups.map(headerGroup => (
            <tr
              style={{ borderRadius: '5px' }}
              {...headerGroup.getHeaderGroupProps()}
            >
              {headerGroup.headers.map(column => (
                <display.StyledTh
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                >
                  <display.FlexContainer>
                    {column.render('Header')}
                    {/* Add a sort direction indicator */}
                    {!column.disableSortBy && (
                      <display.SortIcon>
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <CMS.Icon
                              name="Sort desc"
                              color={theme.background}
                              hoverColor={theme.callToActionAlternate}
                              style={{ height: 20, padding: 5 }}
                            />
                          ) : (
                            <CMS.Icon
                              name="Sort asc"
                              color={theme.background}
                              hoverColor={theme.callToActionAlternate}
                              style={{ height: 20, padding: 5 }}
                            />
                          )
                        ) : (
                          <CMS.Icon
                            name="Sortable"
                            color={theme.background}
                            hoverColor={theme.callToActionAlternate}
                            style={{ height: 20, padding: 5 }}
                          />
                        )}
                      </display.SortIcon>
                    )}
                  </display.FlexContainer>
                </display.StyledTh>
              ))}
            </tr>
          ))}
        </display.StyledThead>
        <display.StyledTbody {...getTableBodyProps()}>
          {page.map(row => {
            prepareRow(row)
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map(cell => {
                  return (
                    <display.StyledTd {...cell.getCellProps()}>
                      {cell.render('Cell')}
                    </display.StyledTd>
                  )
                })}
              </tr>
            )
          })}
        </display.StyledTbody>
      </display.StyledTable>
      <display.Pagination>
        <display.BottomRow>
          <display.PageLengthSelect
            value={pageSize}
            onChange={e => {
              setPageSize(Number(e.target.value))
            }}
          >
            {[10, 20, 30, 40, 50].map(pageSize => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </display.PageLengthSelect>
          <display.NumResults>
            {resources.length} total results
          </display.NumResults>
        </display.BottomRow>
        <display.BottomRow>
          <display.PageInfo>
            Page{' '}
            <strong>
              {pageIndex + 1} of {pageOptions.length}
            </strong>{' '}
          </display.PageInfo>
          <display.NavButtons>
            <display.NavigateButton
              onClick={() => gotoPage(0)}
              disabled={!canPreviousPage}
              aria-label="Go to first page"
            >
              {'<<'}
            </display.NavigateButton>{' '}
            <display.NavigateButton
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
              aria-label="Go to previous page"
            >
              {'<'}
            </display.NavigateButton>{' '}
            <display.NavigateButton
              onClick={() => nextPage()}
              disabled={!canNextPage}
              aria-label="Go to next page"
            >
              {'>'}
            </display.NavigateButton>{' '}
            <display.NavigateButton
              onClick={() => gotoPage(pageCount - 1)}
              disabled={!canNextPage}
              aria-label="Go to last page"
            >
              {'>>'}
            </display.NavigateButton>{' '}
          </display.NavButtons>
        </display.BottomRow>
      </display.Pagination>
    </>
  )
}

export default Table
